/**
 * MetaEmbeddedSignupDialog - Dialog para conexão OAuth com WhatsApp Cloud API via Meta Embedded Signup
 * 
 * Este componente implementa o fluxo de Embedded Signup da Meta para conectar
 * uma conta WhatsApp Business diretamente à plataforma, sem necessidade de
 * intermediários como NotificaMe.
 * 
 * Fluxo:
 * 1. Carrega o Facebook SDK dinamicamente
 * 2. Inicializa FB.init com APP_ID
 * 3. Chama FB.login com config_id (fluxo completo com suporte a coexistência)
 * 4. Escuta evento WA_EMBEDDED_SIGNUP via window.postMessage
 * 5. Recebe code e dados do WABA selecionado
 * 6. Chama edge function meta-waba-exchange-token para trocar code por access_token
 * 7. Credenciais são salvas automaticamente na inbox_credentials
 * 
 * @release v1.0 - 2026-01-16
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { 
  MessageCircle, 
  CheckCircle2, 
  AlertCircle,
  Loader2,
  Info,
  ExternalLink,
  ShieldCheck,
  Globe,
  Smartphone
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { logger } from '@/lib/logger';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// ============= TIPOS =============

/**
 * Props do componente MetaEmbeddedSignupDialog
 */
interface MetaEmbeddedSignupDialogProps {
  /** Controla se o dialog está aberto */
  open: boolean;
  /** Callback para alterar estado de abertura do dialog */
  onOpenChange: (open: boolean) => void;
  /** ID da inbox a ser conectada */
  inboxId: string;
  /** Nome da inbox para exibição */
  inboxName: string;
  /** Callback chamado quando conexão é concluída com sucesso */
  onSuccess: () => void;
}

/**
 * Estados possíveis do processo de conexão
 */
type ConnectionState = 'loading_sdk' | 'idle' | 'connecting' | 'exchanging' | 'success' | 'error';

/**
 * Dados recebidos do evento WA_EMBEDDED_SIGNUP
 * Suporta eventos FINISH e FINISH_ONLY_WABA
 */
interface EmbeddedSignupData {
  type: string;
  event: string; // 'FINISH' | 'FINISH_ONLY_WABA' | 'CANCEL' | 'ERROR'
  data: {
    phone_number_id?: string;
    waba_id?: string;
    display_phone_number?: string;
    business_name?: string;
    business_id?: string; // Presente em FINISH_ONLY_WABA
  };
}

/**
 * Resposta do FB.login
 */
interface FacebookAuthResponse {
  status: string;
  authResponse?: {
    code?: string;
    accessToken?: string;
    userID?: string;
    expiresIn?: number;
    signedRequest?: string;
  };
}

/**
 * Dados do WABA recebidos via postMessage no evento WA_EMBEDDED_SIGNUP
 * Usado para validar completude dos dados antes de chamar exchange-token
 */
interface WabaData {
  waba_id: string | null;
  phone_number_id: string | null;
  display_phone_number: string | null;
  business_name: string | null;
}

/**
 * Tipos de erro identificados no fluxo de Embedded Signup
 * Usado para categorizar e exibir mensagens específicas ao usuário
 */
type EmbeddedSignupErrorType = 
  | 'popup_blocked'        // Popup bloqueado pelo navegador
  | 'user_cancelled'       // Usuário fechou popup sem completar
  | 'no_code'              // Login OK mas sem code (problema de config)
  | 'sdk_error'            // Erro no SDK do Facebook
  | 'network_error'        // Erro de rede
  | 'waba_data_incomplete' // Dados WABA não recebidos ou incompletos via postMessage
  | 'unknown';             // Erro desconhecido

/**
 * Interface para erros detalhados com diagnóstico
 * Fornece informações claras ao usuário sobre como resolver o problema
 */
interface DetailedError {
  /** Tipo categorizado do erro */
  type: EmbeddedSignupErrorType;
  /** Mensagem principal do erro */
  message: string;
  /** Lista de possíveis causas do erro */
  possibleCauses: string[];
  /** Ações sugeridas com links opcionais */
  actions: {
    label: string;
    url?: string;
  }[];
}

// ============= CONSTANTES =============

// ID do App Meta (lido da edge function, mas precisamos para o SDK)
// Nota: Este valor é público e seguro para estar no frontend
const META_APP_ID = '675aborapegar'; // Placeholder - será lido dinamicamente

// ============= COMPONENTE =============

/**
 * Componente de dialog para conexão via Meta Embedded Signup
 */
export function MetaEmbeddedSignupDialog({
  open,
  onOpenChange,
  inboxId,
  inboxName,
  onSuccess,
}: MetaEmbeddedSignupDialogProps) {
  // Estado da conexão
  const [connectionState, setConnectionState] = useState<ConnectionState>('loading_sdk');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [detailedError, setDetailedError] = useState<DetailedError | null>(null);
  const [webhookInfo, setWebhookInfo] = useState<{ verifyToken: string; webhookUrl: string } | null>(null);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [metaAppId, setMetaAppId] = useState<string | null>(null);
  const [metaConfigId, setMetaConfigId] = useState<string | null>(null);
  
  // Estado para armazenar dados do WABA recebidos via evento WA_EMBEDDED_SIGNUP
  const [wabaData, setWabaData] = useState<{
    waba_id: string | null;
    phone_number_id: string | null;
    display_phone_number: string | null;
    business_name: string | null;
  }>({
    waba_id: null,
    phone_number_id: null,
    display_phone_number: null,
    business_name: null,
  });
  
  // Ref para acessar wabaData no callback assíncrono
  const wabaDataRef = useRef(wabaData);
  wabaDataRef.current = wabaData;

  // ============= EFEITOS =============

  /**
   * Busca configurações da Meta quando o dialog abre
   */
  useEffect(() => {
    if (open) {
      fetchMetaConfig();
    }
  }, [open]);

  /**
   * Carrega o Facebook SDK quando dialog abre e temos as configs
   */
  useEffect(() => {
    if (open && metaAppId && !sdkLoaded) {
      loadFacebookSDK();
    }
  }, [open, metaAppId, sdkLoaded]);

  /**
   * Configura listener para mensagens do popup do Facebook
   */
  useEffect(() => {
    if (!open) return;

    const handleMessage = (event: MessageEvent) => {
      // DEBUG: Log todas as mensagens de qualquer origem do Facebook
      if (
        event.origin.includes('facebook.com') || 
        event.origin.includes('staticxx.facebook.com')
      ) {
        console.log('[META-DEBUG] Window message received from:', event.origin);
        console.log('[META-DEBUG] Message data type:', typeof event.data);
        console.log('[META-DEBUG] Message data preview:', 
          typeof event.data === 'string' 
            ? event.data.substring(0, 300) 
            : JSON.stringify(event.data).substring(0, 300)
        );
      }
      
      // Verifica se é uma mensagem do Facebook (inclui staticxx.facebook.com para iframe)
      if (
        event.origin !== 'https://www.facebook.com' && 
        event.origin !== 'https://web.facebook.com' &&
        event.origin !== 'https://staticxx.facebook.com'
      ) {
        return;
      }

      try {
        // CORREÇÃO: event.data pode vir como string JSON ou objeto
        let data: EmbeddedSignupData;
        
        if (typeof event.data === 'string') {
          try {
            data = JSON.parse(event.data);
            console.log('[META-DEBUG] Parsed message as JSON:', { type: data?.type, event: data?.event });
            logger.info('MetaEmbeddedSignupDialog', 'Evento recebido como string, parsed JSON', { 
              type: data?.type, 
              event: data?.event 
            });
          } catch {
            // Não é JSON válido, ignorar silenciosamente
            console.log('[META-DEBUG] Message is not valid JSON, ignoring');
            logger.debug('MetaEmbeddedSignupDialog', 'Mensagem não é JSON válido, ignorando');
            return;
          }
        } else {
          data = event.data as EmbeddedSignupData;
          console.log('[META-DEBUG] Message is already object:', { type: data?.type, event: data?.event });
        }
        
        // Log para debug - todas as mensagens do Facebook
        console.log('[META-DEBUG] Facebook message processed:', { 
          type: data?.type, 
          event: data?.event,
          hasWabaId: !!data?.data?.waba_id,
          hasPhoneNumberId: !!data?.data?.phone_number_id
        });
        
        logger.info('MetaEmbeddedSignupDialog', 'Mensagem do Facebook recebida', { 
          origin: event.origin,
          type: data?.type, 
          event: data?.event,
          hasData: !!data?.data,
          dataKeys: data?.data ? Object.keys(data.data) : []
        });
        
        // ============= SUPORTE A FINISH, FINISH_ONLY_WABA E COEXISTÊNCIA (v22.0) =============
        // A Meta pode enviar três tipos de evento de conclusão:
        // - FINISH: Fluxo completo com phone_number_id incluído
        // - FINISH_ONLY_WABA: Apenas WABA foi compartilhado, phone_number_id será buscado via API
        // - FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING: Coexistência - número do WA Business App (v22.0)
        const isFinishEvent = data?.type === 'WA_EMBEDDED_SIGNUP' && 
          (data?.event === 'FINISH' || 
           data?.event === 'FINISH_ONLY_WABA' || 
           data?.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING');
        
        if (isFinishEvent) {
          const eventType = data.event;
          logger.info('MetaEmbeddedSignupDialog', `Evento WA_EMBEDDED_SIGNUP ${eventType} recebido!`, data);
          
          // Validação imediata do que foi recebido via postMessage
          const receivedData = data.data;
          logger.debug('MetaEmbeddedSignupDialog', 'WABA Data recebido via postMessage', {
            eventType,
            waba_id: receivedData?.waba_id || 'AUSENTE',
            phone_number_id: receivedData?.phone_number_id || 'AUSENTE (será buscado via API)',
            business_id: receivedData?.business_id || 'AUSENTE',
            display_phone_number: receivedData?.display_phone_number || 'AUSENTE',
            business_name: receivedData?.business_name || 'AUSENTE',
            allKeys: receivedData ? Object.keys(receivedData) : []
          });
          
          // Log especial para FINISH_ONLY_WABA
          if (eventType === 'FINISH_ONLY_WABA') {
            logger.info('MetaEmbeddedSignupDialog', 
              'Evento FINISH_ONLY_WABA detectado - phone_number_id será resolvido via Graph API');
          }
          
          // Alerta se waba_id estiver ausente (único campo verdadeiramente obrigatório)
          if (!receivedData?.waba_id) {
            logger.warn('MetaEmbeddedSignupDialog', 'ATENÇÃO: WABA ID ausente no evento', {
              eventType,
              hasWabaId: false
            });
          }
          
          // Salva dados do WABA no estado para usar na troca de token
          // phone_number_id é opcional aqui - será buscado pelo backend se não vier
          if (data.data) {
            const newWabaData = {
              waba_id: data.data.waba_id || null,
              phone_number_id: data.data.phone_number_id || null, // Pode ser null no FINISH_ONLY_WABA
              display_phone_number: data.data.display_phone_number || null,
              business_name: data.data.business_name || null,
            };
            setWabaData(newWabaData);
            wabaDataRef.current = newWabaData; // Atualiza ref imediatamente
            logger.info('MetaEmbeddedSignupDialog', 'Dados WABA salvos com sucesso', {
              ...newWabaData,
              needsPhoneResolution: !newWabaData.phone_number_id
            });
          }
        }
        
        // ============= TRATAMENTO DE EVENTOS DE ERRO/CANCELAMENTO =============
        if (data?.type === 'WA_EMBEDDED_SIGNUP' && data?.event === 'CANCEL') {
          logger.warn('MetaEmbeddedSignupDialog', 'Usuário cancelou o Embedded Signup');
        }
        
        if (data?.type === 'WA_EMBEDDED_SIGNUP' && data?.event === 'ERROR') {
          logger.error('MetaEmbeddedSignupDialog', 'Erro no Embedded Signup', data?.data);
        }
      } catch (err) {
        logger.warn('MetaEmbeddedSignupDialog', 'Erro ao processar mensagem do Facebook', err);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [open]);

  /**
   * Reseta estado quando dialog fecha
   */
  useEffect(() => {
    if (!open) {
      setConnectionState('loading_sdk');
      setErrorMessage(null);
      setDetailedError(null);
      setWebhookInfo(null);
      // Reset dados WABA ao fechar dialog
      setWabaData({
        waba_id: null,
        phone_number_id: null,
        display_phone_number: null,
        business_name: null,
      });
    }
  }, [open]);

  // ============= FUNÇÃO DE DIAGNÓSTICO DE ERRO =============

  /**
   * Diagnostica o erro do FB.login e retorna informações detalhadas
   * para ajudar o usuário a resolver o problema de configuração
   * 
   * @param response - Resposta do FB.login
   * @returns DetailedError com diagnóstico completo
   */
  const diagnoseLoginError = (response: FacebookAuthResponse): DetailedError => {
    // Caso 1: Status 'not_authorized' - usuário negou permissões
    if (response.status === 'not_authorized') {
      return {
        type: 'user_cancelled',
        message: 'Permissões negadas',
        possibleCauses: [
          'Você negou as permissões solicitadas durante o login',
          'É necessário autorizar todas as permissões para conectar o WhatsApp Business'
        ],
        actions: [
          { label: 'Tentar novamente e aceitar todas as permissões' }
        ]
      };
    }

    // Caso 2: Status 'unknown' - popup fechado ou bloqueado
    if (response.status === 'unknown') {
      return {
        type: 'popup_blocked',
        message: 'Popup bloqueado ou fechado',
        possibleCauses: [
          'O popup do Facebook foi bloqueado pelo navegador',
          'Você fechou o popup antes de completar o login',
          'Extensões do navegador (AdBlock, etc.) podem estar interferindo'
        ],
        actions: [
          { label: 'Permitir popups para este site nas configurações do navegador' },
          { label: 'Desativar bloqueadores de anúncios temporariamente' }
        ]
      };
    }

    // Caso 3: Status 'connected' mas sem code - problema de configuração do Meta App
    if (response.status === 'connected' && !response.authResponse?.code) {
      return {
        type: 'no_code',
        message: 'Configuração do Meta App incompleta',
        possibleCauses: [
          'O App Meta pode estar em modo "Development" (somente admins podem conectar)',
          'Faltam permissões Advanced Access para whatsapp_business_management',
          'O domínio atual não está em "Allowed Domains for JavaScript SDK"',
          'O domínio atual não está em "Valid OAuth Redirect URIs"',
          'O Config ID (Embedded Signup) pode estar inválido ou expirado',
          'response_type não está configurado corretamente no App'
        ],
        actions: [
          { 
            label: 'Verificar configurações do App', 
            url: 'https://developers.facebook.com/apps' 
          },
          { 
            label: 'Documentação Meta Embedded Signup', 
            url: 'https://developers.facebook.com/docs/whatsapp/embedded-signup' 
          },
          { 
            label: 'Verificar permissões do App', 
            url: 'https://developers.facebook.com/docs/permissions' 
          }
        ]
      };
    }

    // Caso 4: Tem authResponse mas está vazio
    if (response.authResponse && !response.authResponse.code && !response.authResponse.accessToken) {
      return {
        type: 'sdk_error',
        message: 'Resposta incompleta do Facebook',
        possibleCauses: [
          'O Facebook SDK retornou uma resposta vazia',
          'Pode haver um problema temporário com a Meta',
          'A configuração do App pode estar incorreta'
        ],
        actions: [
          { 
            label: 'Verificar status da Meta', 
            url: 'https://metastatus.com' 
          },
          { label: 'Aguardar alguns minutos e tentar novamente' }
        ]
      };
    }

    // Caso 5: Erro desconhecido
    return {
      type: 'unknown',
      message: 'Erro inesperado durante conexão',
      possibleCauses: [
        'Problema temporário com os servidores da Meta',
        'A configuração do Meta App pode precisar de revisão',
        'Pode haver um problema de rede ou conectividade'
      ],
      actions: [
        { label: 'Tentar novamente em alguns minutos' },
        { 
          label: 'Verificar status da Meta', 
          url: 'https://metastatus.com' 
        },
        { 
          label: 'Documentação de troubleshooting', 
          url: 'https://developers.facebook.com/docs/whatsapp/embedded-signup/troubleshooting' 
        }
      ]
    };
  };

  /**
   * Diagnostica problemas com dados WABA recebidos via postMessage
   * Identifica exatamente quais campos estão faltando para ajudar o usuário
   * 
   * @param wabaData - Dados do WABA recebidos (ou não) via postMessage
   * @returns DetailedError com diagnóstico completo
   */
  const diagnoseWabaDataError = (wabaData: WabaData): DetailedError => {
    const missingFields: string[] = [];
    
    // Campos obrigatórios
    if (!wabaData.waba_id) missingFields.push('WABA ID');
    if (!wabaData.phone_number_id) missingFields.push('Phone Number ID');
    
    // Campos opcionais mas importantes
    const optionalMissing: string[] = [];
    if (!wabaData.display_phone_number) optionalMissing.push('Número de telefone');
    if (!wabaData.business_name) optionalMissing.push('Nome do negócio');

    // Determinar a mensagem baseado no que está faltando
    let message: string;
    let possibleCauses: string[];
    
    if (missingFields.length === 2) {
      // Nenhum dado obrigatório recebido
      message = 'Nenhum dado do WhatsApp Business foi recebido';
      possibleCauses = [
        'O popup do WhatsApp Business foi fechado antes de completar a configuração',
        'Você pode ter cancelado durante a seleção do número ou WABA',
        'Houve um erro de comunicação entre o popup e a aplicação',
        'O evento WA_EMBEDDED_SIGNUP não foi disparado corretamente pela Meta'
      ];
    } else if (missingFields.length > 0) {
      // Dados parciais recebidos
      message = `Dados obrigatórios não recebidos: ${missingFields.join(', ')}`;
      possibleCauses = [
        'O processo foi interrompido antes de selecionar todos os dados necessários',
        'Problema de comunicação com o servidor da Meta',
        'O número de telefone pode não estar corretamente vinculado ao WABA'
      ];
    } else {
      // Apenas campos opcionais faltando (não deveria chegar aqui)
      message = 'Alguns dados do WhatsApp não foram recebidos';
      possibleCauses = [
        'Informações parciais foram recebidas',
        'Pode haver um atraso na comunicação com a Meta'
      ];
    }

    // Log adicional para debug
    if (optionalMissing.length > 0) {
      logger.warn('MetaEmbeddedSignupDialog', 'Campos opcionais faltando no WABA data', { optionalMissing });
    }

    return {
      type: 'waba_data_incomplete',
      message,
      possibleCauses,
      actions: [
        { label: 'Tentar novamente - complete todo o processo sem fechar o popup' },
        { 
          label: 'Verificar WABAs no Meta Business', 
          url: 'https://business.facebook.com/settings/whatsapp-business-accounts' 
        },
        { 
          label: 'Documentação Embedded Signup', 
          url: 'https://developers.facebook.com/docs/whatsapp/embedded-signup' 
        }
      ]
    };
  };

  // ============= FUNÇÕES =============

  /**
   * Busca configurações META do backend
   */
  const fetchMetaConfig = async () => {
    try {
      // Chama edge function para obter APP_ID e CONFIG_ID (valores públicos)
      const { data, error } = await supabase.functions.invoke('meta-waba-config', {
        body: { action: 'get_public_config' },
      });

      if (error) {
        logger.error('MetaEmbeddedSignupDialog', 'Erro ao buscar config Meta', error);
        setErrorMessage('Erro ao carregar configurações. Tente novamente.');
        setConnectionState('error');
        return;
      }

      if (data?.app_id && data?.config_id) {
        setMetaAppId(data.app_id);
        setMetaConfigId(data.config_id);
        logger.info('MetaEmbeddedSignupDialog', 'Configurações carregadas', { 
          app_id: data.app_id.substring(0, 6) + '...',
          config_id: data.config_id.substring(0, 6) + '...'
        });
      } else {
        setErrorMessage('Configurações Meta não encontradas. Entre em contato com o suporte.');
        setConnectionState('error');
      }
    } catch (err) {
      logger.error('MetaEmbeddedSignupDialog', 'Erro ao buscar config', err);
      setErrorMessage('Erro de conexão. Tente novamente.');
      setConnectionState('error');
    }
  };

  /**
   * Carrega o Facebook SDK dinamicamente
   */
  const loadFacebookSDK = useCallback(() => {
    // Verifica se já está carregado
    if ((window as any).FB) {
      initializeFacebookSDK();
      return;
    }

    logger.info('MetaEmbeddedSignupDialog', 'Carregando Facebook SDK');

    // Cria elemento script
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/pt_BR/sdk.js';
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';

    // Callback quando SDK carrega
    (window as any).fbAsyncInit = () => {
      initializeFacebookSDK();
    };

    script.onerror = () => {
      logger.error('MetaEmbeddedSignupDialog', 'Erro ao carregar Facebook SDK');
      setErrorMessage('Erro ao carregar Facebook SDK. Verifique sua conexão.');
      setConnectionState('error');
    };

    document.body.appendChild(script);
  }, [metaAppId]);

  /**
   * Inicializa o Facebook SDK com APP_ID
   */
  const initializeFacebookSDK = () => {
    if (!metaAppId) {
      logger.error('MetaEmbeddedSignupDialog', 'META_APP_ID não disponível');
      setErrorMessage('Configuração incompleta. Entre em contato com o suporte.');
      setConnectionState('error');
      return;
    }

    try {
      // v22.0: Habilita suporte nativo a coexistência no Embedded Signup
      (window as any).FB.init({
        appId: metaAppId,
        cookie: true,
        xfbml: true,
        version: 'v22.0',
      });

      logger.info('MetaEmbeddedSignupDialog', 'Facebook SDK inicializado');
      setSdkLoaded(true);
      setConnectionState('idle');
    } catch (err) {
      logger.error('MetaEmbeddedSignupDialog', 'Erro ao inicializar FB SDK', err);
      setErrorMessage('Erro ao inicializar SDK. Tente recarregar a página.');
      setConnectionState('error');
    }
  };

  /**
   * Inicia o fluxo de login do Facebook com Embedded Signup
   */
  const handleStartSignup = () => {
    // DEBUG: Log detalhado ao iniciar signup
    console.log('[META-DEBUG] handleStartSignup CALLED');
    console.log('[META-DEBUG] sdkLoaded:', sdkLoaded);
    console.log('[META-DEBUG] FB exists:', !!(window as any).FB);
    console.log('[META-DEBUG] metaConfigId:', metaConfigId ? metaConfigId.substring(0, 10) + '...' : 'NULL');
    
    if (!sdkLoaded || !(window as any).FB || !metaConfigId) {
      console.log('[META-DEBUG] BLOCKED: Requisitos não atendidos para FB.login');
      setErrorMessage('SDK não carregado. Aguarde ou recarregue a página.');
      return;
    }

    logger.info('MetaEmbeddedSignupDialog', 'Iniciando FB.login', { inboxId, inboxName });
    setConnectionState('connecting');
    setErrorMessage(null);

    try {
      console.log('[META-DEBUG] Chamando FB.login...');
      
      (window as any).FB.login(
        (response: FacebookAuthResponse) => {
          console.log('[META-DEBUG] >>> FB.login CALLBACK RECEIVED <<<');
          handleFacebookResponse(response);
        },
        {
          config_id: metaConfigId,
          response_type: 'code',
          override_default_response_type: true,
          extras: {
            setup: {},
            // featureType: 'whatsapp_business_app_onboarding' habilita fluxo de coexistência (QR code)
            featureType: 'whatsapp_business_app_onboarding',
            sessionInfoVersion: 3,
          },
        }
      );
      
      console.log('[META-DEBUG] FB.login chamado (popup deve abrir)');
    } catch (err) {
      console.error('[META-DEBUG] FB.login ERROR:', err);
      logger.error('MetaEmbeddedSignupDialog', 'Erro ao chamar FB.login', err);
      setErrorMessage('Erro ao abrir popup do Facebook. Permita popups e tente novamente.');
      setConnectionState('error');
    }
  };

  /**
   * Aguarda dados do WABA estarem disponíveis (máx 3 segundos)
   * O evento WA_EMBEDDED_SIGNUP pode chegar antes ou depois do callback FB.login
   * 
   * IMPORTANTE: Agora aceita eventos FINISH_ONLY_WABA onde phone_number_id pode não vir
   * O único campo obrigatório aqui é waba_id - phone_number_id será resolvido pelo backend
   */
  const waitForWabaData = async (maxWait = 3000): Promise<boolean> => {
    const start = Date.now();
    while (Date.now() - start < maxWait) {
      // CORREÇÃO: Apenas waba_id é obrigatório no frontend
      // phone_number_id será buscado pela edge function se não vier
      if (wabaDataRef.current.waba_id) {
        return true;
      }
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    return false;
  };

  /**
   * Processa a resposta do Facebook após login
   */
  const handleFacebookResponse = async (response: FacebookAuthResponse) => {
    // DEBUG: Logs detalhados para diagnóstico
    console.log('[META-DEBUG] >>> handleFacebookResponse CALLED <<<');
    console.log('[META-DEBUG] response RAW:', JSON.stringify(response, null, 2));
    console.log('[META-DEBUG] response.status:', response.status);
    console.log('[META-DEBUG] authResponse exists:', !!response.authResponse);
    
    if (response.authResponse) {
      console.log('[META-DEBUG] authResponse.code exists:', !!response.authResponse.code);
      console.log('[META-DEBUG] authResponse.code preview:', response.authResponse.code?.substring(0, 30) + '...');
      console.log('[META-DEBUG] authResponse.accessToken exists:', !!response.authResponse.accessToken);
      console.log('[META-DEBUG] authResponse.userID:', response.authResponse.userID);
      console.log('[META-DEBUG] authResponse.expiresIn:', response.authResponse.expiresIn);
    } else {
      console.log('[META-DEBUG] authResponse is NULL/undefined');
    }
    
    // Log detalhado da resposta completa do Facebook
    logger.info('MetaEmbeddedSignupDialog', 'Resposta FB.login COMPLETA', { 
      status: response.status,
      hasAuthResponse: !!response.authResponse,
      hasCode: !!response.authResponse?.code,
      hasAccessToken: !!response.authResponse?.accessToken,
      userID: response.authResponse?.userID,
      expiresIn: response.authResponse?.expiresIn
    });

    // ============= CORREÇÃO: Priorizar existência do CODE sobre STATUS =============
    // No Embedded Signup da Meta, o status pode ser 'unknown' mesmo quando o signup foi bem-sucedido
    // O que realmente importa é ter recebido o CODE para trocar por access_token
    const hasCode = !!response.authResponse?.code;
    
    console.log('[META-DEBUG] Verificação de code:', { 
      hasCode, 
      status: response.status,
      codePreview: hasCode ? response.authResponse?.code?.substring(0, 30) + '...' : 'N/A'
    });
    
    // Só é erro se NÃO tem code - independente do status
    if (!hasCode) {
      console.log('[META-DEBUG] FALHA: Code ausente - tratando como erro');
      
      // Diagnóstico detalhado do erro para ajudar o usuário
      const diagnosedError = diagnoseLoginError(response);
      
      logger.warn('MetaEmbeddedSignupDialog', 'Login falhou - diagnóstico detalhado', { 
        status: response.status,
        errorType: diagnosedError.type,
        hasAuthResponse: !!response.authResponse,
        hasCode: false,
        hasAccessToken: !!response.authResponse?.accessToken
      });
      
      setDetailedError(diagnosedError);
      setConnectionState('error');
      return;
    }
    
    // SUCESSO: Temos o code! Log detalhado antes de prosseguir
    console.log('[META-DEBUG] SUCESSO: Code obtido - prosseguindo para troca de token');
    logger.info('MetaEmbeddedSignupDialog', 'Embedded Signup concluído - code obtido', { 
      status: response.status,
      codePreview: response.authResponse!.code.substring(0, 20) + '...',
      hasWabaData: !!(wabaDataRef.current.waba_id && wabaDataRef.current.phone_number_id)
    });

    // Temos o code! Agora troca por access_token
    setConnectionState('exchanging');

    try {
      const code = response.authResponse.code;
      
      // Aguarda dados do WABA estarem disponíveis (evento WA_EMBEDDED_SIGNUP)
      logger.info('MetaEmbeddedSignupDialog', 'Aguardando dados WABA...');
      const hasWabaData = await waitForWabaData();
      
      if (!hasWabaData) {
        // Diagnóstico detalhado do que está faltando nos dados WABA
        const wabaError = diagnoseWabaDataError(wabaDataRef.current);
        
        logger.error('MetaEmbeddedSignupDialog', 'Dados WABA incompletos ou ausentes', {
          wabaData: wabaDataRef.current,
          missingWabaId: !wabaDataRef.current.waba_id,
          errorType: wabaError.type
        });
        
        setDetailedError(wabaError);
        setConnectionState('error');
        return;
      }
      
      // Validação final: apenas waba_id é obrigatório no frontend
      // phone_number_id será resolvido pelo backend via Graph API se não vier
      const { waba_id, phone_number_id } = wabaDataRef.current;
      if (!waba_id || typeof waba_id !== 'string' || waba_id.trim() === '') {
        const wabaError = diagnoseWabaDataError(wabaDataRef.current);
        logger.error('MetaEmbeddedSignupDialog', 'Validação final falhou: waba_id inválido', { waba_id });
        setDetailedError(wabaError);
        setConnectionState('error');
        return;
      }
      
      // Log informativo se phone_number_id não veio (será buscado pelo backend)
      const needsPhoneResolution = !phone_number_id || typeof phone_number_id !== 'string' || phone_number_id.trim() === '';
      if (needsPhoneResolution) {
        logger.info('MetaEmbeddedSignupDialog', 
          'phone_number_id não veio via postMessage - será resolvido pelo backend via Graph API');
      }
      
      logger.info('MetaEmbeddedSignupDialog', 'Dados WABA validados, chamando edge function', {
        waba_id: wabaDataRef.current.waba_id,
        phone_number_id: wabaDataRef.current.phone_number_id || '(será buscado)',
        needsPhoneResolution
      });
      
      // Chama edge function para trocar code por token com todos os dados
      // phone_number_id é opcional - o backend buscará via Graph API se não vier
      const { data, error } = await supabase.functions.invoke('meta-waba-exchange-token', {
        body: {
          code,
          inbox_id: inboxId,
          waba_id: wabaDataRef.current.waba_id,
          phone_number_id: wabaDataRef.current.phone_number_id || null, // Opcional
          display_phone_number: wabaDataRef.current.display_phone_number,
          business_name: wabaDataRef.current.business_name,
        },
      });

      if (error) {
        logger.error('MetaEmbeddedSignupDialog', 'Erro na troca de token', error);
        setErrorMessage(`Erro ao conectar: ${error.message}`);
        setConnectionState('error');
        return;
      }

      // Tratamento especial: se precisa selecionar número (múltiplos números no WABA)
      if (data?.needs_phone_selection) {
        logger.info('MetaEmbeddedSignupDialog', 'WABA possui múltiplos números - exibindo seleção', {
          phoneNumbers: data.phone_numbers?.length
        });
        // TODO: Fase 3 - Implementar UI de seleção de número
        // Por enquanto, usar o primeiro número automaticamente
        if (data.phone_numbers && data.phone_numbers.length > 0) {
          logger.info('MetaEmbeddedSignupDialog', 'Usando primeiro número automaticamente', {
            selected: data.phone_numbers[0]
          });
        }
      }

      if (!data?.success) {
        setErrorMessage(data?.error || 'Erro desconhecido ao conectar.');
        setConnectionState('error');
        return;
      }

      logger.info('MetaEmbeddedSignupDialog', 'Conexão realizada com sucesso!', {
        phoneResolved: data.phone_resolved_from_api || false
      });
      
      // Salva informações do webhook para exibir ao usuário
      setWebhookInfo({
        verifyToken: data.data?.webhook_verify_token,
        webhookUrl: data.data?.webhook_url,
      });

      setConnectionState('success');
      toast.success('WhatsApp Business conectado com sucesso!');

    } catch (err) {
      logger.error('MetaEmbeddedSignupDialog', 'Erro ao processar resposta', err);
      setErrorMessage('Erro de conexão. Tente novamente.');
      setConnectionState('error');
    }
  };

  /**
   * Confirma sucesso e fecha o dialog
   */
  const handleConfirmSuccess = () => {
    onSuccess();
    onOpenChange(false);
  };

  /**
   * Tenta novamente após erro
   * Limpa estados de erro e reinicia o fluxo
   */
  const handleRetry = () => {
    setErrorMessage(null);
    setDetailedError(null);
    if (sdkLoaded) {
      setConnectionState('idle');
    } else {
      setConnectionState('loading_sdk');
      loadFacebookSDK();
    }
  };

  // ============= RENDERIZAÇÃO =============

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-500/10">
              <MessageCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            Conectar WhatsApp Cloud API
          </DialogTitle>
          <DialogDescription>
            Conecte sua conta do WhatsApp Business à inbox "{inboxName}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">

          {/* Estado: Carregando SDK */}
          {connectionState === 'loading_sdk' && (
            <div className="text-center space-y-4 py-6">
              <div className="flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
              <div className="space-y-1">
                <p className="font-medium">Carregando...</p>
                <p className="text-sm text-muted-foreground">
                  Preparando conexão com Meta Business
                </p>
              </div>
            </div>
          )}

          {/* Estado: Aguardando início */}
          {connectionState === 'idle' && (
            <>
              {/* Instruções */}
              <div className="space-y-3 text-sm text-muted-foreground">
                <p className="font-medium text-foreground">O que você precisa:</p>
                <div className="grid grid-cols-1 gap-2">
                  <div className="flex items-start gap-2 p-2 rounded-lg bg-muted/50">
                    <ShieldCheck className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Conta Meta Business Suite verificada</span>
                  </div>
                  <div className="flex items-start gap-2 p-2 rounded-lg bg-muted/50">
                    <Smartphone className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Número de telefone dedicado para WhatsApp Business</span>
                  </div>
                  <div className="flex items-start gap-2 p-2 rounded-lg bg-muted/50">
                    <Globe className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
                    <span>Acesso à sua conta do Facebook</span>
                  </div>
                </div>
              </div>

              {/* Aviso importante */}
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-700 dark:text-amber-300">
                  <p className="font-medium">Importante:</p>
                  <p className="mt-1">
                    O número conectado não poderá ser usado no WhatsApp pessoal enquanto 
                    estiver vinculado à API.
                  </p>
                </div>
              </div>

              {/* Botão para iniciar */}
              <Button 
                onClick={handleStartSignup}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Conectar com Facebook Business
              </Button>
            </>
          )}

          {/* Estado: Conectando (popup aberto) */}
          {connectionState === 'connecting' && (
            <div className="text-center space-y-4 py-6">
              <div className="flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
              <div className="space-y-1">
                <p className="font-medium">Aguardando autorização...</p>
                <p className="text-sm text-muted-foreground">
                  Complete o processo na janela do Facebook.
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                Selecione sua conta WhatsApp Business e autorize o acesso.
              </p>
            </div>
          )}

          {/* Estado: Trocando token */}
          {connectionState === 'exchanging' && (
            <div className="text-center space-y-4 py-6">
              <div className="flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
              </div>
              <div className="space-y-1">
                <p className="font-medium">Finalizando conexão...</p>
                <p className="text-sm text-muted-foreground">
                  Configurando credenciais e webhook.
                </p>
              </div>
            </div>
          )}

          {/* Estado: Sucesso */}
          {connectionState === 'success' && (
            <div className="space-y-4">
              <div className="text-center space-y-3 py-2">
                <div className="flex justify-center">
                  <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="font-medium text-green-600 dark:text-green-400">
                    Conexão realizada com sucesso!
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Sua conta WhatsApp Business está conectada.
                  </p>
                </div>
              </div>

              {/* Info sobre webhook - só exibe se ainda precisa configurar */}
              {webhookInfo && (
                <div className="space-y-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-blue-700 dark:text-blue-300">
                      <p className="font-medium">Configuração do Webhook:</p>
                      <p className="mt-1">
                        Configure o webhook no Meta Developers com os dados abaixo:
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2 mt-2">
                    <div className="p-2 rounded bg-background/80 text-xs font-mono break-all">
                      <span className="text-muted-foreground">URL: </span>
                      {webhookInfo.webhookUrl}
                    </div>
                    <div className="p-2 rounded bg-background/80 text-xs font-mono break-all">
                      <span className="text-muted-foreground">Token: </span>
                      {webhookInfo.verifyToken}
                    </div>
                  </div>
                </div>
              )}

              <Button 
                onClick={handleConfirmSuccess}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Concluir
              </Button>
            </div>
          )}

          {/* Estado: Erro - com diagnóstico detalhado */}
          {connectionState === 'error' && (
            <div className="space-y-4 py-4">
              {/* Ícone e título do erro */}
              <div className="text-center space-y-2">
                <div className="flex justify-center">
                  <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                    <AlertCircle className="h-6 w-6 text-destructive" />
                  </div>
                </div>
                <p className="font-medium text-destructive">
                  {detailedError?.message || 'Erro na conexão'}
                </p>
              </div>

              {/* Possíveis causas - exibidas em lista visual */}
              {detailedError?.possibleCauses && detailedError.possibleCauses.length > 0 && (
                <div className="space-y-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <p className="text-xs font-medium text-amber-700 dark:text-amber-300 flex items-center gap-1">
                    <Info className="h-3 w-3" />
                    Possíveis causas:
                  </p>
                  <ul className="text-xs text-amber-600 dark:text-amber-400 space-y-1.5 ml-4">
                    {detailedError.possibleCauses.map((cause, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-amber-500 mt-0.5">•</span>
                        <span>{cause}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Links de ação para resolver o problema */}
              {detailedError?.actions && detailedError.actions.some(a => a.url) && (
                <div className="space-y-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <p className="text-xs font-medium text-blue-700 dark:text-blue-300 flex items-center gap-1">
                    <ExternalLink className="h-3 w-3" />
                    Links úteis:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {detailedError.actions
                      .filter(action => action.url)
                      .map((action, idx) => (
                        <a
                          key={idx}
                          href={action.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:underline px-2 py-1 rounded bg-blue-500/10"
                        >
                          <ExternalLink className="h-3 w-3" />
                          {action.label}
                        </a>
                      ))}
                  </div>
                </div>
              )}

              {/* Botão tentar novamente */}
              <Button 
                onClick={handleRetry}
                variant="outline"
                className="w-full"
              >
                Tentar Novamente
              </Button>

              {/* Fallback para erro genérico (sem diagnóstico) */}
              {!detailedError && errorMessage && (
                <p className="text-xs text-muted-foreground text-center">
                  {errorMessage}
                </p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
